import { useMemo, useState } from 'react';
import { faCheck, faChevronDown, faPlus } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { MAX_ATTORNEY_NAME_LENGTH } from '@/components/reviews-hub/constants';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Icon } from '@/components/ui/icon';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { AttorneyOption } from '../types';

export function AttorneyPickerInput({
  id,
  attorneys,
  valueId,
  valueName,
  onSelect,
  errorText,
}: {
  id: string;
  attorneys: AttorneyOption[];
  valueId: string;
  valueName: string;
  onSelect: (selection: { attorneyId: string; attorneyName: string }) => void;
  errorText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const trimmedQuery = query.trim();
  const selectedAttorney = attorneys.find((attorney) => attorney.id === valueId);
  const selectedLabel = selectedAttorney?.name || valueName.trim() || 'Select attorney';
  const filteredAttorneys = useMemo(() => {
    if (!trimmedQuery) return attorneys;

    const normalizedQuery = trimmedQuery.toLowerCase();
    return attorneys.filter((attorney) => attorney.name.toLowerCase().includes(normalizedQuery));
  }, [attorneys, trimmedQuery]);
  const hasExactMatch = attorneys.some(
    (attorney) => attorney.name.toLowerCase() === trimmedQuery.toLowerCase(),
  );
  const showCreateOption = Boolean(trimmedQuery) && !hasExactMatch;

  function handleSelectAttorney(attorney: AttorneyOption) {
    onSelect({ attorneyId: attorney.id, attorneyName: attorney.name });
    setQuery('');
    setOpen(false);
  }

  function handleCreateAttorney() {
    onSelect({ attorneyId: '', attorneyName: trimmedQuery });
    setQuery('');
    setOpen(false);
  }

  function handleClearAttorney() {
    onSelect({ attorneyId: '', attorneyName: '' });
    setQuery('');
    setOpen(false);
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Attorney</Label>
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            colorScheme="secondary"
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-invalid={Boolean(errorText) || undefined}
            aria-describedby={errorText ? `${id}-error` : undefined}
            className={cn(
              'w-full justify-between text-left font-normal hover:bg-primary-foreground min-w-0',
              !selectedAttorney && !valueName.trim() && 'text-muted-foreground',
              errorText && 'border-destructive',
            )}
          >
            <span className="truncate min-w-0 flex-1 text-left overflow-hidden">
              {selectedLabel}
            </span>
            <Icon icon={faChevronDown} className="ml-2 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0"
          align="start"
          preventCloseAutoFocus={false}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or add attorney..."
              value={query}
              maxLength={MAX_ATTORNEY_NAME_LENGTH}
              onValueChange={setQuery}
            />
            <CommandList>
              <CommandEmpty>No attorneys found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="no-attorney"
                  onSelect={handleClearAttorney}
                  className="flex items-center gap-2"
                >
                  <span className="min-w-0 flex-1 truncate">None</span>
                  {!valueId && !valueName.trim() ? (
                    <Icon icon={faCheck} className="shrink-0" />
                  ) : null}
                </CommandItem>
                {filteredAttorneys.map((attorney) => {
                  const isSelected = attorney.id === valueId;

                  return (
                    <CommandItem
                      key={attorney.id}
                      value={attorney.name}
                      onSelect={() => handleSelectAttorney(attorney)}
                      className="flex items-center gap-2"
                    >
                      <span className="min-w-0 flex-1 truncate">{attorney.name}</span>
                      {isSelected ? <Icon icon={faCheck} className="shrink-0" /> : null}
                    </CommandItem>
                  );
                })}
                {showCreateOption ? (
                  <CommandItem
                    value={`create-${trimmedQuery}`}
                    onSelect={handleCreateAttorney}
                    className="flex items-center gap-2"
                  >
                    <Icon icon={faPlus} className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate">Add “{trimmedQuery}”</span>
                  </CommandItem>
                ) : null}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {errorText ? (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}
